import { interpolateColor } from '@/helpers';

// Color scale constants
const COLOR_RED = '#DC2626'; // 0% progress
const COLOR_YELLOW = '#EAB308'; // 50% progress (yellow-500)
const COLOR_GREEN = '#22C55E'; // 100% progress
const COLOR_PURPLE = '#7C3AED'; // >102% progress

/**
 * Returns a color based on progress percentage using interpolated color scale
 *
 * Color scale breakpoints:
 * - 0%: Red (#DC2626)
 * - 50%: Yellow (#D97706)
 * - 100%: Green (#22C55E)
 * - >102%: Purple (#7C3AED)
 *
 * @param percentage - Progress percentage (0-100+)
 * @returns Hex color string (e.g., "#22C55E")
 *
 * @example
 * getProgressColor(0)     // → "#DC2626" (red)
 * getProgressColor(25)    // → (gradient red to yellow)
 * getProgressColor(50)    // → "#D97706" (yellow)
 * getProgressColor(75)    // → (gradient yellow to green)
 * getProgressColor(100)   // → "#22C55E" (green)
 * getProgressColor(110)   // → "#7C3AED" (purple)
 */
export function getProgressColor(percentage: number): string {
  if (percentage > 102) {
    return COLOR_PURPLE;
  }

  if (percentage <= 50) {
    const factor = percentage / 50;
    return interpolateColor(COLOR_RED, COLOR_YELLOW, factor);
  }

  if (percentage <= 100) {
    const factor = (percentage - 50) / 50;
    return interpolateColor(COLOR_YELLOW, COLOR_GREEN, factor);
  }

  // 100-102% range: solid green
  return COLOR_GREEN;
}

// Color scale for ratio-style metrics (e.g. tracked/booked) where the "healthy" zone
// sits near 100% rather than 50%: red at 0%, yellow at 90%, green at 100%+.
const RATIO_COLOR_RED = '#DC2626';
const RATIO_COLOR_YELLOW = '#EAB308';
const RATIO_COLOR_GREEN = '#22C55E';
const RATIO_YELLOW_BREAKPOINT = 90;

/**
 * Returns a color for a 0-100 ratio where 0% is red, 90% is yellow, and 100% (or above)
 * is green — e.g. tracked-hours ÷ booked-hours, which is "good" only near full coverage.
 *
 * @example
 * getRatioColor(0)   // → "#DC2626" (red)
 * getRatioColor(45)  // → (gradient red to yellow)
 * getRatioColor(90)  // → "#EAB308" (yellow)
 * getRatioColor(95)  // → (gradient yellow to green)
 * getRatioColor(100) // → "#22C55E" (green)
 */
export function getRatioColor(percentage: number): string {
  const clamped = Math.max(0, Math.min(percentage, 100));

  if (clamped <= RATIO_YELLOW_BREAKPOINT) {
    const factor = clamped / RATIO_YELLOW_BREAKPOINT;
    return interpolateColor(RATIO_COLOR_RED, RATIO_COLOR_YELLOW, factor);
  }

  const factor = (clamped - RATIO_YELLOW_BREAKPOINT) / (100 - RATIO_YELLOW_BREAKPOINT);
  return interpolateColor(RATIO_COLOR_YELLOW, RATIO_COLOR_GREEN, factor);
}
